'use client'

import * as React from 'react'
import { Home, MapPin } from 'lucide-react'
import { useI18n } from '@/lib/i18n'

// LOCATION block — keeps the production Google Maps embed, restyled to the
// design's shape-md container (detail.jsx:57-82). The design's LocationMap
// placeholder (grid backdrop + 40px primary circle + address pill) renders
// UNDERNEATH the iframe: while the embed loads — or when it fails to paint
// (expired key, network) — the placeholder shows through, so the section
// degrades gracefully instead of showing a blank box.
export interface LocationBlockProps {
  street?: string
  district?: string
  city?: string
  fullAddress: string
  mapEmbedSrc: string | null
  mapLink: string
}

const placeholderBackdrop = [
  'radial-gradient(200px 130px at 24% 32%, rgba(34,197,94,0.09), transparent 70%)',
  'radial-gradient(240px 160px at 76% 70%, rgba(34,197,94,0.07), transparent 70%)',
  'repeating-linear-gradient(0deg, transparent 0 44px, rgb(var(--outline-variant)) 44px 45px)',
  'repeating-linear-gradient(90deg, transparent 0 44px, rgb(var(--outline-variant)) 44px 45px)',
].join(', ')

export function LocationBlock({ street, district, city, fullAddress, mapEmbedSrc, mapLink }: LocationBlockProps) {
  const { t } = useI18n()
  const [embedFailed, setEmbedFailed] = React.useState(false)
  const pill = [street, district].filter(Boolean).join(', ') || fullAddress

  return (
    <div>
      <div className="relative h-[250px] overflow-hidden rounded-shape-md border border-outline-variant bg-surface-low">
        {/* Design LocationMap placeholder — visible until the embed paints. */}
        <div className="absolute inset-0" style={{ background: placeholderBackdrop }} />
        <div className="absolute left-1/2 top-1/2 z-[1] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary shadow-e2">
            <Home size={20} color="#fff" />
          </span>
          {pill && (
            <span className="rounded-full border border-outline bg-surface px-3 py-[5px] text-[12.5px] font-semibold shadow-e1">
              {pill}
            </span>
          )}
        </div>
        <div className="absolute bottom-[10px] left-3 z-[1] font-mono text-[10.5px] uppercase tracking-[1px] text-ink-muted">
          {city || 'Praha'} · {t('listingDetail.mapPreview', 'map preview')}
        </div>

        {mapEmbedSrc && !embedFailed && (
          <iframe
            title="Listing location map"
            src={mapEmbedSrc}
            className="absolute inset-0 z-[2] h-full w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            onError={() => setEmbedFailed(true)}
          />
        )}
      </div>

      <a
        href={mapLink}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2.5 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-ink-secondary transition-colors hover:text-ink"
      >
        <MapPin className="h-4 w-4" />
        <span>{t('listingDetail.openInGoogleMaps', 'Open in Google Maps')}</span>
      </a>
    </div>
  )
}
